import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCents, formatDate } from "@/lib/api";
import { PageHeader, Card, Pill, Loader } from "@/components/ui/Primitives";
import {
  Users2, Clock, Heart, AlertTriangle, CheckCircle2, ArrowRight, Sparkles,
  TrendingUp, Send,
} from "lucide-react";
import { toast } from "sonner";

const BUCKET_TONES = {
  expired: { ring: "ring-rose-200", chip: "bg-rose-100 text-rose-700", label: "Expired" },
  renew_now: { ring: "ring-amber-200", chip: "bg-amber-100 text-amber-700", label: "Renew now" },
  renew_soon: { ring: "ring-sky-200", chip: "bg-sky-100 text-sky-700", label: "Renew soon" },
  watch: { ring: "ring-violet-200", chip: "bg-violet-100 text-violet-700", label: "Watch" },
  stable: { ring: "ring-emerald-200", chip: "bg-emerald-100 text-emerald-700", label: "Stable" },
};

export default function Retention() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const reload = () => {
    setLoading(true);
    api.get("/retention/overview")
      .then((r) => setD(r.data))
      .catch(() => toast.error("Couldn't load retention overview"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  if (loading) return <Loader />;
  if (!d) return null;

  const filtered = (d.rows || []).filter((r) => {
    if (filter === "all") return true;
    if (filter === "attention") return r.score < 60;
    return r.bucket === filter;
  });

  const k = d.kpis || {};

  return (
    <div data-testid="retention-page">
      <PageHeader
        title="Long-term tenants"
        subtitle="Lock in dependable rent. Every active lease scored on payment history, tenure, maintenance load, and contact recency."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile testid="retention-kpi-renew-now" label="Renew now" value={k.renew_now} sub="≤30 days to lease end" tone="amber" icon={Clock} onClick={() => setFilter("renew_now")} />
        <KpiTile testid="retention-kpi-renew-soon" label="Renew soon" value={k.renew_soon} sub="31-60 days" tone="sky" icon={Send} onClick={() => setFilter("renew_soon")} />
        <KpiTile testid="retention-kpi-attention" label="Need attention" value={k.needs_attention} sub="Score below 60" tone="rose" icon={AlertTriangle} onClick={() => setFilter("attention")} />
        <KpiTile testid="retention-kpi-stable" label="Stable & happy" value={k.stable} sub="Score 80+" tone="emerald" icon={Heart} onClick={() => setFilter("stable")} />
      </div>

      <Card className="p-5 mb-5 bg-gradient-to-r from-emerald-50 via-white to-sky-50 border-emerald-100">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-emerald-200 text-emerald-700 flex items-center justify-center flex-none">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-display text-base font-bold text-slate-900">Why retention matters</div>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              The cheapest tenant you&apos;ll ever sign is the one already living there. Replacing a tenant typically costs 1-2 months&apos; rent in vacancy + marketing + cleaning + screening. Average tenure across your portfolio: <strong className="text-emerald-700">{k.avg_tenure_months} months</strong>.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2 mb-5">
        {[
          ["all", `All leases (${k.total_leases || 0})`],
          ["renew_now", `Renew now (${k.renew_now || 0})`],
          ["renew_soon", `Renew soon (${k.renew_soon || 0})`],
          ["attention", `Needs attention (${k.needs_attention || 0})`],
          ["stable", `Stable (${k.stable || 0})`],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`retention-filter-${key}`}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filter === key
                ? "bg-emerald-600 text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        // Special-case: zero leases at all → guide the landlord to create
        // one instead of just showing "no matches", which was confusing.
        k.total_leases === 0 ? (
          <Card className="p-10 text-center" data-testid="retention-empty-no-leases">
            <Users2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <div className="font-display text-lg font-bold text-slate-900">No active leases yet</div>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              This tab scores every active lease on payment history, tenure, and contact
              recency. Once you have at least one active lease, your retention buckets
              will populate automatically.
            </p>
            <div className="mt-5 inline-flex flex-wrap items-center gap-2 justify-center">
              <Link to="/leases" data-testid="retention-empty-cta-leases"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm">
                Create a lease <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/applications" data-testid="retention-empty-cta-applications"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-sm">
                View applications
              </Link>
            </div>
          </Card>
        ) : (
          <Card className="p-10 text-center" data-testid="retention-empty">
            <Users2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <div className="font-display text-lg font-bold text-slate-900">No leases match this filter</div>
            <p className="text-sm text-slate-500 mt-2">Try the &quot;All leases&quot; tab to see your full roster.</p>
          </Card>
        )
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {filtered.map((r) => (
            <LeaseRow key={r.lease_id} r={r} onTouched={reload} />
          ))}
        </div>
      )}
    </div>
  );
}

function KpiTile({ testid, label, value, sub, tone = "emerald", icon: Icon, onClick }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-left p-5 rounded-xl bg-white border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
          <div className="mt-2 font-display text-2xl font-bold text-slate-900 tabular-nums">{value ?? 0}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </button>
  );
}

function LeaseRow({ r, onTouched }) {
  const tone = BUCKET_TONES[r.bucket] || BUCKET_TONES.watch;
  const [logging, setLogging] = useState(false);
  const action = r.suggested_action || {};

  const logTouch = async () => {
    setLogging(true);
    try {
      await api.post("/retention/touch", { lease_id: r.lease_id, kind: action.key || "manual" });
      toast.success("Logged — you reached out");
      onTouched?.();
    } catch {
      toast.error("Couldn't log");
    } finally { setLogging(false); }
  };

  return (
    <Card className={`p-5 ring-1 ${tone.ring}`} data-testid={`retention-row-${r.lease_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${tone.chip}`}>{tone.label}</span>
            <span className="text-xs text-slate-500 tabular-nums">{r.days_to_lease_end >= 0 ? `${r.days_to_lease_end}d to expiry` : `${Math.abs(r.days_to_lease_end)}d overdue`}</span>
          </div>
          <div className="font-display text-base font-bold text-slate-900 truncate">{r.tenant_name || r.tenant_email || "Tenant"}</div>
          <div className="text-xs text-slate-500 truncate">{r.property_name || "Property"} · {formatCents(r.monthly_rent_cents)}/mo · {r.months_in_unit} months in unit</div>
        </div>
        <ScoreRing score={r.score} />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {(r.drivers || []).map((d, i) => (
          <span key={i} className={`text-xs px-2 py-1 rounded-md ${d.tone === "good" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
            {d.tone === "good" ? "↑" : "↓"} {d.label}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="text-xs text-slate-500">
          Lease: {formatDate(r.start_date)} → {formatDate(r.end_date)}
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={logTouch} disabled={logging}
            data-testid={`retention-touch-${r.lease_id}`}
            className="px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 rounded-md hover:bg-slate-50">
            {logging ? "…" : "Mark touched"}
          </button>
          <Link to={action.route || "/leases"} data-testid={`retention-action-${r.lease_id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
            {action.label || "Open"} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </Card>
  );
}

function ScoreRing({ score = 50 }) {
  const pct = Math.max(0, Math.min(100, score));
  const color = pct >= 80 ? "#10b981" : pct >= 60 ? "#0ea5e9" : pct >= 40 ? "#f59e0b" : "#f43f5e";
  const r = 22;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <div className="relative w-14 h-14 flex-none">
      <svg viewBox="0 0 56 56" className="w-14 h-14 -rotate-90">
        <circle cx="28" cy="28" r={r} fill="none" stroke="#e2e8f0" strokeWidth="5" />
        <circle cx="28" cy="28" r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-900 tabular-nums">{pct}</div>
    </div>
  );
}
